import React from 'react';
import { FaLanguage, FaClock, FaCertificate } from 'react-icons/fa';
import { BsCurrencyRupee } from 'react-icons/bs';
import { HiOutlineArrowRight } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

const CourseCard = ({ course, index }) => {
    const navigate = useNavigate();

    // Truncate text to specific length
    const truncateText = (text, maxLength) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Calculate discount percentage
    const discountPercent = (course?.mrp && course?.mrp > 0 && course?.price && course?.price > 0 && course.mrp > course.price)
        ? Math.round(((course.mrp - course.price) / course.mrp) * 100)
        : 0;

    // Get category info
    const subCategory = course?.subCategoryDto?.name || course?.subCategoryName || 'General';
    const category = course?.subCategoryDto?.categoryDto?.name || 'Course';

    return (
        <div
            style={styles.card}
            onClick={() => {
                navigate("/courseDetails/" + course.id);
            }}
            onMouseEnter={(e) => {
                const card = e.currentTarget;
                card.style.transform = 'translateY(-10px)';
                card.style.boxShadow = '0 24px 48px rgba(2, 64, 118, 0.18)';
                const img = card.querySelector('.card-image');
                if (img) img.style.transform = 'scale(1.08)';
                const arrow = card.querySelector('.arrow-icon');
                if (arrow) {
                    arrow.style.transform = 'translateX(4px)';
                    arrow.style.opacity = '1';
                }
            }}
            onMouseLeave={(e) => {
                const card = e.currentTarget;
                card.style.transform = 'translateY(0)';
                card.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                const img = card.querySelector('.card-image');
                if (img) img.style.transform = 'scale(1)';
                const arrow = card.querySelector('.arrow-icon');
                if (arrow) {
                    arrow.style.transform = 'translateX(0)';
                    arrow.style.opacity = '0.7';
                }
            }}
        >
            {/* Card Image with overlay and badges */}
            <div style={styles.imageContainer}>
                <img
                    className="card-image"
                    src={course?.thumbnailUrl}
                    alt={course?.title || "Course Thumbnail"}
                    style={styles.image}
                />
                {/* Gradient Overlay */}
                <div style={styles.imageOverlay}></div>

                {/* Category Badge - Top Left on Image */}
                <div style={styles.categoryBadgeOnImage}>
                    <span style={styles.categoryMain}>{subCategory}</span>
                    {category && category !== subCategory && (
                        <span style={styles.categorySub}>• {category}</span>
                    )}
                </div>

                {/* Free or Discount Badge - Top Right */}
                {course?.price === 0 ? (
                    <div style={styles.freeBadge}>FREE</div>
                ) : discountPercent > 0 && (
                    <div style={styles.discountBadge}>{discountPercent}% OFF</div>
                )}

                {/* Duration on Image */}
                <div style={styles.durationOnImage}>
                    <FaClock style={styles.durationIcon} />
                    <span>{course?.duration || 0}h</span>
                </div>
            </div>

            {/* Course Content */}
            <div style={styles.content}>
                {/* Title */}
                <h3 style={styles.title}>
                    {truncateText(course?.title, 55)}
                </h3>

                {/* Description */}
                <p style={styles.description}>
                    {truncateText(course?.shortDesc, 90)}
                </p>

                {/* Course Meta Row */}
                <div style={styles.metaRow}>
                    <div style={styles.metaItem}>
                        <FaLanguage style={styles.metaIcon} />
                        <span>{truncateText(course?.language, 18) || 'N/A'}</span>
                    </div>
                    <div style={styles.metaItem}>
                        <FaCertificate style={styles.metaIconCert} />
                        <span>Certificate</span>
                    </div>
                </div>

                {/* Footer with Price and CTA */}
                <div style={styles.footer}>
                    <div style={styles.priceSection}>
                        {course?.price === 0 ? (
                            <span style={styles.freePrice}>Free Course</span>
                        ) : (
                            <>
                                <div style={styles.currentPrice}>
                                    <BsCurrencyRupee style={styles.rupeeIcon} />
                                    <span>{course?.price?.toLocaleString('en-IN')}</span>
                                </div>
                                {course?.mrp && course.mrp > 0 && course.mrp > (course.price || 0) && (
                                    <div style={styles.mrpPrice}>
                                        <BsCurrencyRupee style={styles.mrpRupeeIcon} />
                                        <span>{course.mrp.toLocaleString('en-IN')}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div style={styles.ctaButton}>
                        <span>Explore</span>
                        <HiOutlineArrowRight className="arrow-icon" style={styles.arrowIcon} />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Professional Modern Styles
const styles = {
    card: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '380px',
        minHeight: '440px',
        backgroundColor: '#ffffff',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        margin: '0 auto',
        border: '1px solid rgba(2, 64, 118, 0.06)',
    },
    imageContainer: {
        position: 'relative',
        width: '100%',
        height: '200px',
        overflow: 'hidden',
        backgroundColor: '#f1f5f9',
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
    imageOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '60%',
        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, transparent 100%)',
        pointerEvents: 'none',
    },
    categoryBadgeOnImage: {
        position: 'absolute',
        top: '14px',
        left: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        padding: '6px 12px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    },
    categoryMain: {
        fontSize: '11px',
        fontWeight: '700',
        color: '#024076',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    categorySub: {
        fontSize: '10px',
        fontWeight: '500',
        color: '#64748b',
        textTransform: 'uppercase',
    },
    freeBadge: {
        position: 'absolute',
        top: '14px',
        right: '14px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#fff',
        padding: '6px 14px',
        borderRadius: '8px',
        fontSize: '11px',
        fontWeight: '800',
        letterSpacing: '0.5px',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
    },
    discountBadge: {
        position: 'absolute',
        top: '14px',
        right: '14px',
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: '#fff',
        padding: '6px 12px',
        borderRadius: '8px',
        fontSize: '11px',
        fontWeight: '800',
        letterSpacing: '0.3px',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.35)',
    },
    durationOnImage: {
        position: 'absolute',
        bottom: '14px',
        right: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        color: '#fff',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
    },
    durationIcon: {
        fontSize: '12px',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
        padding: '20px',
        gap: '10px',
    },
    title: {
        fontSize: '17px',
        fontWeight: '700',
        color: '#0f172a',
        margin: '0',
        lineHeight: '1.4',
        minHeight: '48px',
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        letterSpacing: '-0.2px',
    },
    description: {
        fontSize: '13px',
        color: '#64748b',
        margin: '0',
        lineHeight: '1.55',
        minHeight: '40px',
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginTop: '4px',
        paddingTop: '12px',
        borderTop: '1px solid #f1f5f9',
    },
    metaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: '#475569',
        fontWeight: '500',
    },
    metaIcon: {
        fontSize: '15px',
        color: '#024076',
    },
    metaIconCert: {
        fontSize: '13px',
        color: '#f59e0b',
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 'auto',
        paddingTop: '16px',
    },
    priceSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    currentPrice: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '22px',
        fontWeight: '800',
        color: '#024076',
        letterSpacing: '-0.5px',
    },
    rupeeIcon: {
        fontSize: '18px',
        marginRight: '1px',
    },
    mrpPrice: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '13px',
        color: '#94a3b8',
        textDecoration: 'line-through',
    },
    mrpRupeeIcon: {
        fontSize: '11px',
    },
    freePrice: {
        fontSize: '18px',
        fontWeight: '800',
        color: '#10b981',
        letterSpacing: '-0.3px',
    },
    ctaButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        background: 'linear-gradient(135deg, #024076 0%, #0b5394 100%)',
        color: '#fff',
        padding: '10px 18px',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: '600',
        letterSpacing: '0.2px',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 12px rgba(2, 64, 118, 0.25)',
    },
    arrowIcon: {
        fontSize: '14px',
        transition: 'all 0.3s ease',
        opacity: 0.7,
    },
};

export default CourseCard;